// src/pages/admin/FundDetailsPage.js - גרסה פשוטה עם טעינה מנתונים שמורים בלבד
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase/firebaseConfig';
import adminAPI from '../../services/adminApi';
import { 
  ArrowLeft, Edit3, Save, X, Plus, Trash2,
  TrendingUp, TrendingDown, DollarSign, Calendar,
  Building, Target, BarChart3, PieChart, Info,
  CheckCircle, AlertCircle, Copy, ExternalLink,
  Loader, Search
} from 'lucide-react';
import AdminNavbar from '../../components/admin/AdminNavbar';

const FundDetailsPage = () => {
  const { fundId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [fund, setFund] = useState(location.state?.fund || null);
  const [source, setSource] = useState(location.state?.source || 'unknown');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFund, setEditedFund] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [showAddField, setShowAddField] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // בדיקת אימות
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // אם אין מידע על הקרן, טען אותה מהנתונים השמורים
    if (!fund && fundId) {
      loadFundFromStorage();
    }

    // טעינת שדות מותאמים אישית מ-localStorage
    const savedCustomFields = localStorage.getItem(`fund_${fundId}_custom_fields`);
    if (savedCustomFields) {
      try {
        setCustomFields(JSON.parse(savedCustomFields));
      } catch (error) {
        console.warn('Error loading custom fields:', error);
        setCustomFields([]);
      }
    }

    return () => unsubscribe();
  }, [fundId, fund, navigate]);

  useEffect(() => {
    if (fund) {
      setEditedFund({ ...fund });
    }
  }, [fund]);

  const loadFundFromStorage = async () => {
    setLoading(true);
    setError(null);
    
    try {
    
      // שימוש בפונקציה החדשה לטעינה מהנתונים השמורים
      const result = await adminAPI.getFundById(fundId, source !== 'unknown' ? source : null);
      
      if (result.success) {
      
        setFund(result.fund);
        setSource(result.source);
      } else {
     
        setError(result.error);
      }
      
    } catch (error) {
      console.error(`❌ Error loading fund ${fundId}:`, error);
      setError(`שגיאה בטעינת פרטי הקרן: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatReturn = (value, showIcon = true) => {
    if (value === null || value === undefined) return 'לא זמין';
    const num = parseFloat(value);
    if (isNaN(num)) return 'לא זמין';
    
    const color = num > 0 ? 'text-green-600' : num < 0 ? 'text-red-600' : 'text-gray-600';
    const icon = num > 0 ? TrendingUp : num < 0 ? TrendingDown : null;
    
    return (
      <div className={`flex items-center gap-1 ${color}`}>
        {showIcon && icon && React.createElement(icon, { className: "w-4 h-4" })}
        <span className="font-medium">{num.toFixed(2)}%</span>
      </div>
    );
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return 'לא זמין';
    const num = parseFloat(value);
    if (isNaN(num)) return 'לא זמין';
    
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(2)}B ₪`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M ₪`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K ₪`;
    }
    return `${num.toLocaleString()} ₪`;
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    
    try {
      // שמירת שדות מותאמים אישית
      localStorage.setItem(`fund_${fundId}_custom_fields`, JSON.stringify(customFields));
      
      // עדכון הקרן
      setFund(editedFund);
      setIsEditing(false);
      setSaveStatus('success');
      
      // הסתרת הודעת הצלחה אחרי 3 שניות
      setTimeout(() => setSaveStatus(null), 3000);
      
    } catch (error) {
      console.error('❌ Error saving fund:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const handleCancel = () => {
    setEditedFund({ ...fund });
    setIsEditing(false);
    setSaveStatus(null);
  };

  const handleAddCustomField = () => {
    if (!newFieldName.trim()) return;
    
    const newField = {
      id: Date.now(),
      name: newFieldName.trim(),
      value: newFieldValue.trim(),
      type: 'text',
      createdAt: new Date().toISOString()
    };
    
    setCustomFields([...customFields, newField]);
    setNewFieldName('');
    setNewFieldValue('');
    setShowAddField(false);
  };

  const handleDeleteCustomField = (fieldId) => {
    setCustomFields(customFields.filter(field => field.id !== fieldId));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleRetry = () => {
    setError(null);
    loadFundFromStorage();
  };

  // מסך טעינה
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">טוען פרטי קרן</h3>
            <p className="text-gray-600">מחפש קרן {fundId} בנתונים השמורים...</p>
          </div>
        </div>
      </div>
    );
  }

  // מסך שגיאה
  if (error && !fund) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">קרן לא נמצאה</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-4 h-4" />
                חפש שוב
              </button>
              <button
                onClick={() => navigate('/admin/data')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                חזור לניהול נתונים
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!fund) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">קרן לא נמצאה</h3>
            <p className="text-gray-600 mb-4">לא ניתן לטעון פרטי קרן {fundId}</p>
            <button
              onClick={() => navigate('/admin/data')}
              className="flex items-center gap-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              חזור לניהול נתונים
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* כותרת עם פעולות */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/data')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              חזור
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{fund.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  ID: {fund.id}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  {fund._sourceLabel || fund.sourceLabel || source}
                </span>
                <button
                  onClick={() => copyToClipboard(fund.id)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="העתק מזהה"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* הודעות סטטוס */}
            {saveStatus === 'saving' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                שומר...
              </div>
            )}
            {saveStatus === 'success' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                נשמר בהצלחה
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                שגיאה בשמירה
              </div>
            )}
            
            {isEditing ? (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  שמור
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  ביטול
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                ערוך קרן
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* עמודה ראשית - פרטי הקרן */}
          <div className="lg:col-span-2 space-y-6">
            {/* מידע בסיסי */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                מידע בסיסי
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">שם הקרן</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFund.name || ''}
                      onChange={(e) => setEditedFund({...editedFund, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium break-words">{fund.name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">חברה מנהלת</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedFund.company || ''}
                      onChange={(e) => setEditedFund({...editedFund, company: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 break-words">{fund.company}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">סיווג</label>
                  {isEditing ? (
                    <textarea
                      value={editedFund.classification || ''}
                      onChange={(e) => setEditedFund({...editedFund, classification: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 break-words">{fund.classification}</p>
                  )}
                </div>
              </div>
            </div>

            {/* תשואות */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                תשואות
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">חודש אחרון</p>
                  {formatReturn(fund.returns?.month1)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">שנה עד היום</p>
                  {formatReturn(fund.returns?.ytd)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">12 חודשים</p>
                  {formatReturn(fund.returns?.year1)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">3 שנים</p>
                  {formatReturn(fund.returns?.year3)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">5 שנים</p>
                  {formatReturn(fund.returns?.year5)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">ממוצע 3 שנים</p>
                  {formatReturn(fund.returns?.avgAnnual3Years)}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">ממוצע 5 שנים</p>
                  {formatReturn(fund.returns?.avgAnnual5Years)}
                </div>
              </div>
            </div>

            {/* עלויות */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-500" />
                עלויות
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">דמי ניהול שנתיים</p>
                  <div className="flex items-center gap-1 text-orange-600">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-xl font-bold">
                      {fund.fees?.management ? `${fund.fees.management.toFixed(2)}%` : 'לא זמין'}
                    </span>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">דמי הפקדה</p>
                  <div className="flex items-center gap-1 text-orange-600">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-xl font-bold">
                      {fund.fees?.deposit ? `${fund.fees.deposit.toFixed(2)}%` : 'לא זמין'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* מידע פיננסי */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                מידע פיננסי
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">סך נכסים</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(fund.financialData?.totalAssets || fund.totalAssets)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">הפקדות חודשיות נטו</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(fund.financialData?.netMonthlyDeposits)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">הפקדות</p>
                  <p className="text-lg font-medium text-green-600">
                    {formatCurrency(fund.financialData?.deposits)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">משיכות</p>
                  <p className="text-lg font-medium text-red-600">
                    {formatCurrency(fund.financialData?.withdrawals)}
                  </p>
                </div>
              </div>
            </div>

            {/* מדדי ביצועים */}
            {fund.performance && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-indigo-500" />
                  מדדי ביצועים
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">יחס שארפ</p>
                    <p className="text-lg font-medium text-gray-900">
                      {fund.performance.sharpeRatio || 'לא זמין'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">אלפא</p>
                    <p className="text-lg font-medium text-gray-900">
                      {fund.performance.alpha || 'לא זמין'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">סטיית תקן</p>
                    <p className="text-lg font-medium text-gray-900">
                      {fund.performance.standardDeviation || 'לא זמין'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">נכסים נזילים (%)</p>
                    <p className="text-lg font-medium text-gray-900">
                      {fund.performance.liquidAssetsPercent || 'לא זמין'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* עמודה צדדית - מידע נוסף */}
          <div className="space-y-6">
            {/* פרטי הקרן */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-gray-500" />
                פרטי הקרן
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">תאריך הקמה</p>
                  <p className="font-medium text-gray-900">
                    {fund.fundInfo?.inceptionDate || fund.inceptionDate || 'לא זמין'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">אוכלוסיית יעד</p>
                  <p className="font-medium text-gray-900 break-words">
                    {fund.fundInfo?.targetPopulation || fund.targetPopulation || 'כלל האוכלוסיה'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">התמחות</p>
                  <p className="font-medium text-gray-900 break-words">
                    {fund.fundInfo?.specialization || fund.specialization || 'כללית'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">התמחות משנית</p>
                  <p className="font-medium text-gray-900 break-words">
                    {fund.fundInfo?.subSpecialization || fund.subSpecialization || 'אין'}
                  </p>
                </div>
              </div>
            </div>

            {/* חשיפות השקעה */}
            {fund.exposures && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  חשיפות השקעה
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">חשיפה למניות</span>
                    <span className="font-medium text-gray-900">
                      {fund.exposures.stocks ? `${fund.exposures.stocks}%` : 'לא זמין'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">חשיפה חוץ</span>
                    <span className="font-medium text-gray-900">
                      {fund.exposures.foreign ? `${fund.exposures.foreign}%` : 'לא זמין'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">חשיפת מטבע</span>
                    <span className="font-medium text-gray-900">
                      {fund.exposures.currency ? `${fund.exposures.currency}%` : 'לא זמין'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* שדות מותאמים אישית */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-500" />
                  שדות מותאמים
                </h3>
                <button
                  onClick={() => setShowAddField(true)}
                  className="flex items-center gap-1 px-3 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  הוסף שדה
                </button>
              </div>

              {/* טופס הוספת שדה */}
              {showAddField && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="שם השדה"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <input
                      type="text"
                      placeholder="ערך השדה"
                      value={newFieldValue}
                      onChange={(e) => setNewFieldValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddCustomField}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        הוסף
                      </button>
                      <button
                        onClick={() => {
                          setShowAddField(false);
                          setNewFieldName('');
                          setNewFieldValue('');
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                      >
                        <X className="w-4 h-4" />
                        ביטול
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* רשימת שדות מותאמים */}
              <div className="space-y-2">
                {customFields.length === 0 ? (
                  <p className="text-gray-500 text-sm">אין שדות מותאמים אישית</p>
                ) : (
                  customFields.map((field) => (
                    <div key={field.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{field.name}</p>
                        <p className="text-sm text-gray-600 break-words">{field.value}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteCustomField(field.id)}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* מידע טכני */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                מידע טכני
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">תקופת דיווח</span>
                  <span className="font-medium text-gray-900">
                    {fund.technical?.reportPeriod || fund.reportPeriod || 'לא זמין'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">עדכון אחרון</span>
                  <span className="font-medium text-gray-900">
                    {fund._loadedAt ? 
                      new Date(fund._loadedAt).toLocaleDateString('he-IL') : 
                      (fund.technical?.lastUpdate ? 
                        new Date(fund.technical.lastUpdate).toLocaleDateString('he-IL') : 
                        'לא זמין')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">מקור נתונים</span>
                  <span className="font-medium text-gray-900">
                    {fund._sourceLabel || fund.sourceLabel || source}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">טעינה מ</span>
                  <span className="font-medium text-gray-900">
                    {fund._loadedFrom === 'storage' ? 'נתונים שמורים' : 'API'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundDetailsPage;